import { useCallback, useEffect } from "react";
import LandingPageWrapper from "../../../pages/AppPages/Pages/components/LandingPageWrapper";
import AllOutOfStockScreen, {
  useIsAllOutOfStock,
} from "./LegacyAllOutOfStockScreen";
import CheckoutConnect from "./LegacyCheckoutConnect";
import { CheckoutMainFlow } from "./LegacyCheckoutMainFlow";
import CheckoutMFA from "./LegacyCheckoutMFA";
import CheckoutMFAChoose from "./LegacyCheckoutMFAChoose";
import CheckoutSuccess from "./LegacyCheckoutSuccess";
import EnterCheckoutAnimation from "./LegacyEnterCheckoutAnimation";
import { useNavigate } from "react-router-dom";
import { closeCartDrawer } from "@/redux/reducers/checkout";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  CheckoutProvider,
  useCheckoutContext,
} from "../../CheckoutMachine/CheckoutProvider";
import { MfaValues } from "@/types/misc";
import CheckoutErrorDialog from "./LegacyCheckoutErrorDialog";
import { getAllCarts } from "@/redux/selectors";
import { useSignedIn } from "@/utils/hooks/useSignedIn";
import CheckoutTransition from "../CheckoutTransition";
import { useSelector } from "@xstate/react";
import { useAsyncCheckoutEnabled } from "../CheckoutV2/hooks/useAsyncCheckoutEnabled";
import CheckoutFlowV2 from "../CheckoutV2";

const useEnterCheckoutFlow = () => {
  const { send: checkoutSend, actorRef } = useCheckoutContext();
  const hasCartItems = useAppSelector((state) => getAllCarts(state).length > 0);
  const hasRetailers = useAppSelector((state) => state.retailers.length > 0);
  const { signedIn } = useSignedIn();
  const navigate = useNavigate();
  const checkoutState = useSelector(
    actorRef,
    (snapshot) => snapshot.context.checkoutState
  );

  useEffect(() => {
    if (!signedIn) {
      // replace the current location with the signin page to avoid the user going back to this page using the browser back button
      navigate("/signin", { replace: true });
      return;
    }
    if (checkoutState === "IDLE") {
      if (!hasCartItems || !hasRetailers) {
        navigate("/mybag");
        return;
      }
      checkoutSend({ type: "INITIATE_CHECKOUT" });
    }
  }, [
    signedIn,
    checkoutSend,
    hasCartItems,
    hasRetailers,
    checkoutState,
    navigate,
  ]);

  const resetCheckout = useCallback(() => {
    checkoutSend({ type: "RESET_CHECKOUT" });
  }, [checkoutSend]);

  return resetCheckout;
};

const CheckoutFlow = () => {
  const resetCheckout = useEnterCheckoutFlow();
  const navigate = useNavigate();
  const { actorRef } = useCheckoutContext();

  const { allOutOfStock, isSingleRetailerCheckout } = useIsAllOutOfStock();
  const showAllOutOfStockPage = allOutOfStock && isSingleRetailerCheckout;
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(closeCartDrawer());
  }, [navigate, dispatch]);

  const status = useSelector(
    actorRef,
    (snapshot) => snapshot?.context?.checkoutState
  );

  const otpStatus = useSelector(
    actorRef,
    (snapshot) => snapshot?.context?.otpStatus
  );

  const includeFooter = showAllOutOfStockPage || status === "CHECKOUT_SUCCESS";

  const getBody = () => {
    if (showAllOutOfStockPage) {
      return <AllOutOfStockScreen />;
    }

    if (otpStatus === MfaValues.MfaChoose) {
      return <CheckoutMFAChoose />;
    }

    if (otpStatus === MfaValues.MfaEnter) {
      return <CheckoutMFA />;
    }

    if (status === "LOGIN_SCREEN") {
      return <CheckoutConnect />;
    }

    if (status === "CHECKOUT_IN_PROGRESS") {
      return <EnterCheckoutAnimation />;
    }

    if (
      status === "CHECKOUT_SUMMARY" ||
      status === "CHECKOUT_USER_INPUT_UPDATE_IN_PROGRESS"
    ) {
      return <CheckoutMainFlow />;
    }

    if (status === "CHECKOUT_SUCCESS") {
      return <CheckoutSuccess />;
    }

    return null;
  };

  return (
    <LandingPageWrapper includeFooter={includeFooter}>
      <CheckoutTransition />
      <CheckoutErrorDialog handleReset={resetCheckout} />
      {getBody()}
    </LandingPageWrapper>
  );
};

const CheckoutFlowWithProvider = () => {
  const { enabled } = useAsyncCheckoutEnabled();

  if (enabled) {
    return <CheckoutFlowV2 />;
  }

  return (
    <CheckoutProvider>
      <CheckoutFlow />
    </CheckoutProvider>
  );
};

export default CheckoutFlowWithProvider;
